import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Checkbox, Autocomplete, Typography, ListItemText
} from '@mui/material';
import { PhoneInput } from 'react-international-phone';
// The literal build/ path, not the package's "style.css" export subpath — this
// project's webpack 4 doesn't understand package.json "exports" maps.
import 'react-international-phone/build/index.css';
import { getCountries, getPhoneCountries, getStatesByCountry, useGeo } from '../../../utils/geo';
import MenuItem from "@material-ui/core/MenuItem";
import FormNotice from "./FormNotice.comp";
import EntryModeToggle from "./EntryModeToggle.comp";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {REGIONS} from "../../../utils/META/cat_101";
import {FormControl} from "@material-ui/core";
import {isValidEmail, getPhoneDialCode, splitPhoneByDialCode} from "../../../utils/utils";
import {
    LANGUAGES_SUPPORTED,
    PRIMARY_ACTIVITY_CODE, PRIMARY_ACTIVITY_CODE_VERBOSE,
    ADDITIONAL_CAPABILITY_CODE, ADDITIONAL_CAPABILITY_CODE_VERBOSE,
    ORGANIZATION_TYPE_CODE, ORGANIZATION_TYPE_CODE_VERBOSE,
} from "../../../utils/constants";

/**
 * One empty school row inside a network — slide 36, plus the OrgInstitute
 * fields from "Schema for Data Collection" §3 (a network's school IS an
 * OrgInstitute record, same as a standalone school).
 */
const blankSchool = () => ({
    org_name: '', org_address: '', org_city: '', postal_code: '', org_country: '',
    contact_first_name: '', contact_last_name: '', contact_title: '', contact_email: '',
    number_of_learners: '',
    institute_phone_country_code: '', institute_phone_number: '',
    institute_type: '',
    institute_primary_activities: [],
    institute_address_line1: '',
    institute_address_line2: '',
    institute_state: '',
    institute_website: '',
    institute_additional_capabilities: [],
});

const defaultFields = {
    org_name: '',
    // School / Network shape. A network is a flag on a school-type account,
    // not a separate entity — see is_school_network on the Organization model.
    is_school_network: false,
    // Contact person, split. PW deck slides 35-36 put the same block on
    // both forms: First Name, Last Name, Title / Role (optional), Email.
    contact_first_name: '',
    contact_last_name: '',
    contact_title: '',
    contact_name: '',
    contact_email: '',
    phone_number: '',
    preferred_locale: '',
    org_address: '',
    org_city: '',
    postal_code: '',
    org_country: '',
    callback_url: 'To be Decided',
    callback_key: 'To be Decided',
    // Network-only — "Schema for Data Collection" §2. Kept out of the single
    // school / generic client payload; see handleCreate.
    org_phone_country_code: '',
    org_phone_number: '',
    org_state: '',
    org_website: '',
    // Network address, second line — the network's own address is split into
    // Line 1 (org_address) and Line 2 (optional), per Ian's review comment.
    org_address_line2: '',
    primary_activities: [],
    additional_capabilities: [],
    // Single-school-only — "Schema for Data Collection" §3, stored on the
    // school's OrgInstitute record. Kept out of the network's own top-level
    // payload (a network isn't itself a school institute) and out of the
    // generic client payload; see handleCreate.
    number_of_learners: '',
    institute_phone_country_code: '',
    institute_phone_number: '',
    institute_type: '',
    institute_primary_activities: [],
    institute_address_line1: '',
    institute_address_line2: '',
    institute_state: '',
    institute_website: '',
    institute_additional_capabilities: [],
};

/**
 * Validate the top-level account (the school, or the network itself).
 * Pure, so the same rules drive the inline messages, the live error state and
 * whether the submit button is enabled — there is no second copy to drift.
 */
const validateAccount = (fields, apiAccess, isSingleSchool) => {
    const e = {};
    if (!fields.org_name) e.org_name = 'Required';
    // Both forms carry the same contact block — PW deck slides 35 and 36.
    if (!fields.contact_first_name) e.contact_first_name = 'Required';
    if (!fields.contact_last_name) e.contact_last_name = 'Required';
    if (!fields.contact_email) e.contact_email = 'Required';
    else if (!isValidEmail(fields.contact_email)) e.contact_email = 'Invalid email address';
    // Address is required on BOTH forms — slide 35 shows "School Address *".
    // A single school hides the combined field in favour of the split
    // Address Line 1 / Line 2 pair, so Line 1 carries the requirement instead.
    if (isSingleSchool) {
        if (!fields.institute_address_line1) e.institute_address_line1 = 'Required';
    } else if (!fields.org_address) e.org_address = 'Required';
    if (!fields.org_city) e.org_city = 'Required';
    if (!fields.postal_code) e.postal_code = 'Required';
    if (!fields.org_country) e.org_country = 'Required';
    if (apiAccess) {
        if (!fields.callback_url) e.callback_url = 'Required';
        if (!fields.callback_key) e.callback_key = 'Required';
    }
    return e;
};

/**
 * Has anyone actually put anything in this card?
 *
 * The network form opens with one blank card because adding schools straight
 * away is the usual path. But schools are optional, so an untouched card must
 * not block the form: a partner filling in only the network details would
 * otherwise be stuck behind required fields for a school they never intended
 * to add. A blank card is ignored — not validated, not submitted.
 */
const isBlankSchool = (sc) => Object.values(sc).every((v) => String(v || '').trim() === '');

/** Same, for one school card inside a network. */
const validateSchool = (sc) => {
    const e = {};
    if (!sc.org_name) e.org_name = 'Required';
    // The combined address field is hidden for a network's school card too
    // (see the JSX below) — Line 1 carries the requirement instead, same as
    // the single-school form.
    if (!sc.institute_address_line1) e.institute_address_line1 = 'Required';
    if (!sc.org_city) e.org_city = 'Required';
    if (!sc.postal_code) e.postal_code = 'Required';
    if (!sc.org_country) e.org_country = 'Required';
    if (!sc.contact_first_name) e.contact_first_name = 'Required';
    if (!sc.contact_last_name) e.contact_last_name = 'Required';
    if (!sc.contact_email) e.contact_email = 'Required';
    else if (!isValidEmail(sc.contact_email)) e.contact_email = 'Invalid email address';
    return e;
};


const CreateClientDialog = ({ open, onClose, onSubmit, loading, isSchool, initialIsNetwork = false, allowNetwork = true, onUpload, uploadLabel, manualLabel }) => {
    useGeo(); // re-render with the API's lists once they load
    /**
     * Which shape is being created. PW deck slide 33: "Add a single school, or a
     * network if the schools belong to one group."
     *
     * Only offered on the enterprise/school flow — the older generic "client"
     * flow has no notion of networks.
     */
    const [isNetwork, setIsNetwork] = useState(false);
    const entityLabel = isNetwork ? 'network' : (isSchool ? 'school' : 'client');
    // A single school, as opposed to a network of schools or a generic client —
    // drives which address field is shown/required, among other single-school-
    // only fields below.
    const isSingleSchool = isSchool && !isNetwork;
    const [fields, setFields] = useState(defaultFields);

    /*
     * The schools a network is created with.
     *
     * OPTIONAL. The mock on slide 36 puts a × on School 1 itself, so the card
     * can be removed and a network created with none — which is consistent
     * with the rest of the pack: a network adds schools from its own portal
     * later (Master Flow J02-012), and the partner can add one to an existing
     * network at any time (J02-005).
     *
     * The form still OPENS with one card, because creating a network together
     * with its first schools is the common path (J02-004).
     */
    const [schools, setSchools] = useState([blankSchool()]);

    const addSchool = () => setSchools((list) => [...list, blankSchool()]);
    const removeSchool = (idx) => setSchools((list) => list.filter((_, i) => i !== idx));
    const updateSchool = (idx, key, value) => {
        setSchools((list) => list.map((sc, i) => (i === idx ? { ...sc, [key]: value } : sc)));
        markSchoolTouched(idx, key);
    };

    /** Cards that actually carry data — a blank one is not a school. */
    const filledSchools = schools.filter((sc) => !isBlankSchool(sc));

    /* "1 school in 1 country" — the counter chip on slide 36. */
    const schoolCountLabel = (() => {
        const n = filledSchools.length;
        const countries_n = new Set(filledSchools.map((sc) => sc.org_country).filter(Boolean)).size;
        const schoolWord = n === 1 ? 'school' : 'schools';
        const countryWord = countries_n === 1 ? 'country' : 'countries';
        return `${n} ${schoolWord} in ${countries_n} ${countryWord}`;
    })();
    const [apiAccess, setApiAccess] = useState(false);

    /**
     * Which inputs the user has actually visited. Validation runs on every
     * keystroke, but a message is only shown once the field has been touched
     * (or once Create has been pressed) — otherwise the form is red on open.
     */
    const [touched, setTouched] = useState({});
    const [schoolTouched, setSchoolTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const resetForm = (network) => {
        setFields(defaultFields);
        setApiAccess(false);
        setIsNetwork(network);
        setSchools([blankSchool()]);
        setTouched({});
        setSchoolTouched({});
        setSubmitAttempted(false);
    };

    useEffect(() => {
        if (open) resetForm(!!initialIsNetwork);
    }, [open, initialIsNetwork]);

    /*
     * Switching between Single School and School Network clears the form.
     * The two are different accounts — a name typed as a school's would
     * otherwise be submitted as the network's name.
     */
    const switchMode = (network) => {
        if (network === isNetwork) return;
        resetForm(network);
    };

    /* Live validity — recomputed each render, drives the submit button. */
    const liveErrors = validateAccount(fields, apiAccess, isSingleSchool);
    // Blank cards validate clean, so they neither show errors nor block Create.
    const liveSchoolErrors = isNetwork
        ? schools.map((sc) => (isBlankSchool(sc) ? {} : validateSchool(sc)))
        : [];
    const isFormValid =
        Object.keys(liveErrors).length === 0 &&
        liveSchoolErrors.every((e) => Object.keys(e).length === 0);

    /** Show a field's error only once it has been touched or Create was pressed. */
    const errFor = (name) =>
        (submitAttempted || touched[name]) ? liveErrors[name] : undefined;
    const schoolErrFor = (idx, name) =>
        (submitAttempted || (schoolTouched[idx] || {})[name])
            ? (liveSchoolErrors[idx] || {})[name]
            : undefined;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields(f => ({ ...f, [name]: value }));
        setTouched((t) => ({ ...t, [name]: true }));
    };

    const markSchoolTouched = (idx, key) =>
        setSchoolTouched((t) => ({ ...t, [idx]: { ...(t[idx] || {}), [key]: true } }));

    const handleCreate = () => {
        // Reveal every outstanding message at once, in case the user pressed
        // Create without visiting the empty fields.
        setSubmitAttempted(true);

        if (isFormValid) {
            /*
             * contact_name stays the stored truth the rest of the product reads,
             * composed from the two parts. Both are sent as well so the form can
             * be rendered back exactly.
             */
            const {
                org_phone_country_code, org_phone_number, org_state, org_website,
                org_address_line2,
                primary_activities, additional_capabilities,
                number_of_learners, institute_phone_country_code, institute_phone_number,
                institute_type, institute_primary_activities,
                institute_address_line1, institute_address_line2, institute_state,
                institute_website, institute_additional_capabilities,
                ...accountFields
            } = fields;
            const payload = {
                ...accountFields,
                is_school_network: isNetwork,
                contact_name: `${fields.contact_first_name} ${fields.contact_last_name}`.trim(),
                ...(apiAccess ? {} : { callback_url: '', callback_key: '' }),
                /*
                 * Network-only fields — "Schema for Data Collection" §2. Left off
                 * the single school / generic client payload; they have no place
                 * on those account types.
                 */
                ...(isNetwork ? {
                    org_phone_country_code, org_phone_number, org_address_line2, org_state, org_website,
                    primary_activities, additional_capabilities,
                } : {}),
                /*
                 * Single-school-only fields — "Schema for Data Collection" §3,
                 * stored on the school's OrgInstitute record rather than its
                 * account. institute_head_title / institute_address mirror the
                 * account-level contact_title / org_address the form already
                 * collects — the doc stores them a second time on the institute
                 * record, but a second visible input for the same value would
                 * just confuse the person filling this in.
                 */
                ...(isSingleSchool ? {
                    number_of_learners: number_of_learners === '' ? null : Number(number_of_learners),
                    institute_phone_country_code, institute_phone_number,
                    institute_head_title: fields.contact_title,
                    institute_type,
                    institute_primary_activities,
                    // org_address is hidden for a single school (see the address
                    // field below) — Line 1 is the real address now, so it feeds
                    // institute_address instead of the account-level field.
                    institute_address: fields.institute_address_line1,
                    institute_address_line1,
                    institute_address_line2,
                    institute_state,
                    institute_website,
                    institute_additional_capabilities,
                } : {}),
                /*
                 * Schools travel with the network in ONE request. The API creates
                 * them together and rolls the whole thing back if any fails, so
                 * the single button on slide 38 never leaves a half-made network.
                 */
                ...(isNetwork ? {
                    schools: schools
                        .filter((sc) => !isBlankSchool(sc))
                        .map((sc) => ({
                            ...sc,
                            contact_name: `${sc.contact_first_name} ${sc.contact_last_name}`.trim(),
                            number_of_learners: sc.number_of_learners === '' ? null : Number(sc.number_of_learners),
                            institute_head_title: sc.contact_title,
                            // org_address is hidden on the school card too — Line 1 is
                            // the real address now, so it feeds institute_address.
                            institute_address: sc.institute_address_line1,
                        })),
                } : {}),
            };
            onSubmit(payload);
        }
    };

    /*
     * A click on the dimmed area outside the form is ignored: it used to close
     * the dialog and throw away everything typed (LA-PW-05Oct-2). The form is
     * left with Cancel, the X, or Escape.
     */
    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        resetForm(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm"
                PaperProps={{
                    sx: {
                        border: '2px solid #000000',
                        borderRadius: '15px',
                        overflow: {xs:'scroll',sm:'hidden'},
                    }
                }}>
            <DialogTitle sx={{ color: '#000000',fontsize:'43px', fontWeight: 'bold', textAlign: 'center' }}>
                {/* One heading whichever chip is chosen (LA-PW-07-Oct-03, slide 10). */}
                {isSchool ? 'Add a School or Network' : 'Add a New Client'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ my: 2 }}>
                    {/* The upload sits inside Add, as it does in Add a Class. */}
                    {onUpload && <EntryModeToggle centered active="manual" manualLabel={manualLabel} uploadLabel={uploadLabel} onUpload={onUpload} />}
                    {/*
                      * School or Network. PW deck slide 33 offers both from one
                      * entry point, and slide 37 lets a school join a network.
                      * Enterprise flow only — the generic client flow has no
                      * notion of networks.
                      */}
                    {isSchool && allowNetwork && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {[
                                { value: false, label: 'Single School' },
                                { value: true, label: 'School Network' },
                            ].map((opt) => (
                                <Button
                                    key={opt.label}
                                    onClick={() => switchMode(opt.value)}
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: '10px',
                                        border: '1px solid',
                                        borderColor: isNetwork === opt.value ? '#FF6900' : 'var(--brand-border, #E5E7EB)',
                                        backgroundColor: isNetwork === opt.value ? 'rgba(255,105,0,0.08)' : 'transparent',
                                        color: isNetwork === opt.value ? '#FF6900' : 'var(--brand-text-muted, #6B7280)',
                                    }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {/* Under the chips, so it describes the one that is chosen (slide 10, note 3). */}
                    <Typography sx={{ textAlign: 'center', fontSize: '15px', mb: 2, color: 'var(--brand-text, #111827)' }}>
                        {isNetwork
                            ? 'Register the network and its contacts, then add each school it operates.'
                            : 'Register the school and its main contact.'}
                    </Typography>

                    <TextField
                        label={isNetwork ? 'Network Name' : (isSchool ? 'School Name' : 'Organization Name')}
                        name="org_name"
                        value={fields.org_name}
                        onChange={handleChange}
                        error={!!errFor('org_name')}
                        helperText={errFor('org_name')}
                        fullWidth
                        sx={{ mb: 2 }}
                        autoFocus
                        autoComplete="organization"
                    />

                    {/*
                      * The contact block — identical on both forms. PW deck
                      * slide 35 heads it "Contact person" on a school, slide 36
                      * "Network contact" on a network; the fields are the same
                      * four either way.
                      */}
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "var(--brand-text-muted, #6B7280)", mb: 1 }}>
                        {isNetwork ? "Network contact" : "School contact"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                        <TextField
                            label="First Name"
                            name="contact_first_name"
                            value={fields.contact_first_name}
                            onChange={handleChange}
                            error={!!errFor('contact_first_name')}
                            helperText={errFor('contact_first_name')}
                            fullWidth
                            autoComplete="given-name"
                        />
                        <TextField
                            label="Last Name"
                            name="contact_last_name"
                            value={fields.contact_last_name}
                            onChange={handleChange}
                            error={!!errFor('contact_last_name')}
                            helperText={errFor('contact_last_name')}
                            fullWidth
                            autoComplete="family-name"
                        />
                    </Box>
                    <TextField
                        label="Title / Role (optional)"
                        name="contact_title"
                        value={fields.contact_title}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                        autoComplete="organization-title"
                    />
                    <TextField
                        label="Contact Email"
                        name="contact_email"
                        type="email"
                        value={fields.contact_email}
                        onChange={handleChange}
                        error={!!errFor('contact_email')}
                        helperText={errFor('contact_email')}
                        fullWidth
                        sx={{ mb: 2 }}
                        autoComplete="email"
                    />
                    {/*
                      * Network's phone number is one combined box — dial code shown
                      * inline, typed or picked from the flag dropdown, same as the
                      * library's own demo. "Schema for Data Collection" §2 still wants
                      * the dial code and number as two API fields, so onChange splits
                      * the combined value back apart. Single school / generic client
                      * keep the plain field they always had; out of scope here.
                      */}
                    {isNetwork ? (
                        <Box sx={{ mb: 2 }}>
                            <PhoneInput
                                countries={getPhoneCountries()}
                                defaultCountry="us"
                                placeholder="Network Phone Number"
                                // Without this, the library re-inserts the current country's
                                // dial code the instant the field is cleared to empty — the
                                // field could never actually go blank to be retyped from scratch.
                                disableDialCodePrefill
                                /*
                                 * Deliberately uncontrolled — no `value` prop. Feeding the
                                 * library's own onChange output straight back in as `value` on
                                 * every keystroke fought its internal cursor tracking: each
                                 * render reset the caret to just after "+", so the next digit
                                 * landed at the START of the number instead of the end,
                                 * scrambling it and re-triggering country guessing on every
                                 * keystroke. The create form never needs to seed an existing
                                 * number anyway — it always starts blank.
                                 */
                                // The library's default dropdown z-index (1) loses to sibling
                                // fields painted after it in the same MUI Dialog stacking
                                // context, so it renders half-hidden behind them. Lift it above
                                // the dialog's own content instead of guessing at a global override.
                                countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                onChange={(phone, countryIso2) => {
                                    if (!phone) {
                                        setFields((f) => ({ ...f, org_phone_country_code: '', org_phone_number: '' }));
                                        return;
                                    }
                                    const dialCode = getPhoneDialCode(countryIso2);
                                    setFields((f) => ({
                                        ...f,
                                        org_phone_country_code: dialCode,
                                        org_phone_number: splitPhoneByDialCode(phone, dialCode),
                                    }));
                                }}
                                inputProps={{ name: 'org_phone_number', autoComplete: 'tel' }}
                                inputStyle={{ flex: 1, minWidth: 0 }}
                                style={{
                                    width: '100%',
                                    '--react-international-phone-height': '56px',
                                    '--react-international-phone-font-size': '16px',
                                    '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                }}
                            />
                        </Box>
                    ) : isSchool ? (
                        <Box sx={{ mb: 2 }}>
                            <PhoneInput
                                countries={getPhoneCountries()}
                                defaultCountry="us"
                                placeholder="School Phone Number"
                                disableDialCodePrefill
                                countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                onChange={(phone, countryIso2) => {
                                    if (!phone) {
                                        setFields((f) => ({ ...f, institute_phone_country_code: '', institute_phone_number: '' }));
                                        return;
                                    }
                                    const dialCode = getPhoneDialCode(countryIso2);
                                    setFields((f) => ({
                                        ...f,
                                        institute_phone_country_code: dialCode,
                                        institute_phone_number: splitPhoneByDialCode(phone, dialCode),
                                    }));
                                }}
                                inputProps={{ name: 'institute_phone_number', autoComplete: 'tel' }}
                                inputStyle={{ flex: 1, minWidth: 0 }}
                                style={{
                                    width: '100%',
                                    '--react-international-phone-height': '56px',
                                    '--react-international-phone-font-size': '16px',
                                    '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                }}
                            />
                        </Box>
                    ) : (
                        <TextField
                            label="Phone Number"
                            name="phone_number"
                            type="tel"
                            value={fields.phone_number}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            autoComplete="tel"
                        />
                    )}
                    {/*
                      * Network-only fields — "Schema for Data Collection" §2:
                      * state, website, and the two pick-lists. Recorded only;
                      * additional_capabilities does not change pricing, access
                      * or billing.
                      */}
                    {isNetwork && (
                        <>
                            <TextField
                                label="Network Website (optional)"
                                name="org_website"
                                value={fields.org_website}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                autoComplete="url"
                            />
                            {/* Primary Role / Activity hidden for a network per request. */}
                            {/*
                            <TextField
                                select
                                label="Primary Role / Activity"
                                name="primary_activities"
                                value={fields.primary_activities}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => selected
                                        .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                        .join(', '),
                                }}
                            >
                                {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox checked={fields.primary_activities.indexOf(code) > -1} />
                                        <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                    </MenuItem>
                                ))}
                            </TextField>
                            */}
                            {/* Additional Capabilities hidden for a network per request. */}
                            {/*
                            <TextField
                                select
                                label="Additional Capabilities"
                                name="additional_capabilities"
                                value={fields.additional_capabilities}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => selected
                                        .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                        .join(', '),
                                }}
                            >
                                {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox checked={fields.additional_capabilities.indexOf(code) > -1} />
                                        <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                    </MenuItem>
                                ))}
                            </TextField>
                            */}
                        </>
                    )}
                    {/*
                      * Single-school-only fields — "Schema for Data Collection" §3,
                      * stored on the OrgInstitute record. Not shown for a network's
                      * own top-level details (it isn't a school institute itself —
                      * its schools get the same fields per-card below) or for a
                      * generic non-school client.
                      */}
                    {isSchool && !isNetwork && (
                        <>
                            {/*
                              * Number of Learners and School Organization Type
                              * are hidden for a single school per request —
                              * fields kept in state/payload, just not shown.
                              */}
                            {/*
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    label="Number of Learners"
                                    name="number_of_learners"
                                    type="number"
                                    value={fields.number_of_learners}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    select
                                    label="School Organization Type"
                                    name="institute_type"
                                    value={fields.institute_type}
                                    onChange={handleChange}
                                    fullWidth
                                >
                                    {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                                        <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            */}
                            {/* School Primary Role / Activity hidden for a single school per request. */}
                            {/*
                            <TextField
                                select
                                label="School Primary Role / Activity"
                                name="institute_primary_activities"
                                value={fields.institute_primary_activities}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => selected
                                        .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                        .join(', '),
                                }}
                            >
                                {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox checked={fields.institute_primary_activities.indexOf(code) > -1} />
                                        <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                    </MenuItem>
                                ))}
                            </TextField>
                            */}
                            <TextField
                                label="School Website (optional)"
                                name="institute_website"
                                value={fields.institute_website}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                autoComplete="url"
                            />
                            {/* Additional Capabilities hidden for a single school per request. */}
                            {/*
                            <TextField
                                select
                                label="Additional Capabilities"
                                name="institute_additional_capabilities"
                                value={fields.institute_additional_capabilities}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => selected
                                        .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                        .join(', '),
                                }}
                            >
                                {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                    <MenuItem key={code} value={code}>
                                        <Checkbox checked={fields.institute_additional_capabilities.indexOf(code) > -1} />
                                        <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                    </MenuItem>
                                ))}
                            </TextField>
                            */}
                        </>
                    )}
                    {/*
                      * Preferred Language was hidden per the PW deck (slides
                      * 35, 36, 39: "Hide preferred language for now"), shown
                      * again per a later request, and commented out again for
                      * now.
                      */}
                    {/*
                    <TextField
                        select
                        label="Preferred Language"
                        name="preferred_locale"
                        value={fields.preferred_locale}
                        onChange={handleChange}
                        error={!!errFor('preferred_locale')}
                        helperText={errFor('preferred_locale') || ' '}
                        fullWidth
                    >
                        {Object.entries(LANGUAGES_SUPPORTED).map(([code, name]) => (
                            <MenuItem key={code} value={code}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>
                    */}
                    {/*
                      * Combined address field — kept for Network Address (and
                      * the generic client). A single school hides this in favour
                      * of the split Address Line 1 / Line 2 pair below, with
                      * Line 1 required instead — see validateAccount.
                      */}
                    {/*
                      * A network's own address is Line 1 (org_address, still the
                      * required field) + Line 2 (optional), matching the school
                      * forms — Ian's review comment.
                      */}
                    {!isSingleSchool && (
                        <TextField
                            label={isNetwork ? "Network Address — Line 1" : "School Address"}
                            name="org_address"
                            value={fields.org_address}
                            onChange={handleChange}
                            error={!!errFor('org_address')}
                            helperText={errFor('org_address')}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    )}
                    {/* Network address line 2 — stored on the Organization as org_address_line2. */}
                    {isNetwork && (
                        <TextField
                            label="Network Address — Line 2 (optional)"
                            name="org_address_line2"
                            value={fields.org_address_line2}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    )}
                    {/*
                      * Address line 1/2 sit right under the main address field
                      * so every address-related input reads as one group,
                      * rather than being split off in the institute-only block
                      * above with unrelated fields in between.
                      */}
                    {isSchool && !isNetwork && (
                        <>
                            <TextField
                                label="School Address — Line 1"
                                name="institute_address_line1"
                                value={fields.institute_address_line1}
                                onChange={handleChange}
                                error={!!errFor('institute_address_line1')}
                                helperText={errFor('institute_address_line1')}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="School Address — Line 2 (optional)"
                                name="institute_address_line2"
                                value={fields.institute_address_line2}
                                onChange={handleChange}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                    <Autocomplete
                        options={getCountries()}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) => option.code === value?.code}
                        value={getCountries().find((option) => option.code === fields.org_country) || null}
                        onChange={(event, newValue) => {
                            setFields((f) => ({
                                ...f,
                                org_country: newValue?.code || "",
                                // The state list is keyed by country — a stale code from the
                                // previous country would silently point at the wrong state.
                                org_state: '',
                                institute_state: '',
                            }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Country"
                                variant="outlined"
                                error={!!errFor('org_country')}
                                helperText={errFor('org_country') || " "}
                            />
                        )}
                        fullWidth
                        sx={{ mb: 0 }}
                    />
                    {/*
                      * State/province, below Country since its options depend on which
                      * country is picked — same cascading Autocomplete pattern as the
                      * per-student state picker in AddClassStudentsDialog.jsx, backed by
                      * the same ISO 3166-2 states (the API's geo master, via utils/geo.js).
                      */}
                    {(isNetwork || (isSchool && !isNetwork)) && (() => {
                        const stateKey = isNetwork ? 'org_state' : 'institute_state';
                        const statesForCountry = getStatesByCountry(fields.org_country);
                        return (
                            <Autocomplete
                                options={statesForCountry}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.code === value?.code}
                                value={statesForCountry.find((s) => s.code === fields[stateKey]) || null}
                                onChange={(event, newValue) => {
                                    setFields((f) => ({ ...f, [stateKey]: newValue ? newValue.code : '' }));
                                }}
                                disabled={!fields.org_country || statesForCountry.length === 0}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={isNetwork ? 'Network State / Province / Region' : 'School State / Province / Region'}
                                        variant="outlined"
                                        placeholder={
                                            !fields.org_country
                                                ? 'Select country first'
                                                : statesForCountry.length === 0 ? 'N/A' : 'Select state'
                                        }
                                    />
                                )}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        );
                    })()}
                    <TextField
                        label="City"
                        name="org_city"
                        value={fields.org_city}
                        onChange={handleChange}
                        error={!!errFor('org_city')}
                        helperText={errFor('org_city')}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Postal Code"
                        name="postal_code"
                        value={fields.postal_code}
                        onChange={handleChange}
                        error={!!errFor('postal_code')}
                        helperText={errFor('postal_code')}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    {/* Assessment-via-API access.
                    <FormControlLabel
                        sx={{ mb: 2, mt: 1 }}
                        control={
                            <Checkbox
                                checked={apiAccess}
                                onChange={(e) => setApiAccess(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={`Check here if this ${entityLabel} needs access to the Assessment via API`}
                    />
                    {apiAccess && (
                        <>
                            <TextField
                                label="Callback URL"
                                name="callback_url"
                                value={fields.callback_url}
                                onChange={handleChange}
                                error={!!errFor('callback_url')}
                                helperText={errFor('callback_url')}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Callback Key"
                                name="callback_key"
                                value={fields.callback_key}
                                onChange={handleChange}
                                error={!!errFor('callback_key')}
                                helperText={errFor('callback_key')}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                    */}

                    {/*
                      * "Schools in this network" — PW deck slides 36-38. The
                      * network form carries its schools inline, each numbered,
                      * removable, and with its own contact person. One button
                      * creates all of it; the API does it atomically.
                      */}
                    {isNetwork && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                    Schools in this network
                                </Typography>
                                <Typography sx={{
                                    fontSize: 12, color: 'var(--brand-text-muted, #6B7280)',
                                    border: '1px solid var(--brand-border, #E5E7EB)',
                                    borderRadius: '999px', px: 1.25, py: '2px',
                                }}>
                                    {schoolCountLabel}
                                </Typography>
                            </Box>

                            {schools.length === 0 && (
                                <Box sx={{
                                    border: '1px dashed var(--brand-border, #E5E7EB)', borderRadius: '10px',
                                    p: 2, mb: 2, textAlign: 'center',
                                    fontSize: 13, color: 'var(--brand-text-muted, #6B7280)',
                                }}>
                                    No schools yet — you can add them now, or later from the network row.
                                </Box>
                            )}

                            {schools.map((sc, idx) => (
                                <Box key={idx} sx={{
                                    border: '1px solid var(--brand-border, #E5E7EB)',
                                    borderRadius: '10px', p: 2, mb: 2,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 20, height: 20, borderRadius: '6px',
                                                backgroundColor: 'rgba(255,105,0,0.12)', color: '#FF6900',
                                                fontSize: 12, fontWeight: 700,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            }}>{idx + 1}</Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>School {idx + 1}</Typography>
                                        </Box>
                                        {schools.length > 0 && (
                                            <Button
                                                onClick={() => removeSchool(idx)}
                                                sx={{ minWidth: 0, p: 0.5, color: 'var(--brand-text-muted, #9CA3AF)' }}
                                            >
                                                ×
                                            </Button>
                                        )}
                                    </Box>

                                    <TextField
                                        label="School Name"
                                        value={sc.org_name}
                                        onChange={(e) => updateSchool(idx, 'org_name', e.target.value)}
                                        error={!!schoolErrFor(idx, 'org_name')}
                                        helperText={schoolErrFor(idx, 'org_name')}
                                        fullWidth sx={{ mb: 2 }}
                                    />

                                    <Typography sx={{
                                        fontSize: 12, fontWeight: 700, letterSpacing: '0.4px',
                                        color: 'var(--brand-text-muted, #6B7280)', mb: 1,
                                    }}>
                                        SCHOOL CONTACT PERSON
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <TextField
                                            label="First Name"
                                            value={sc.contact_first_name}
                                            onChange={(e) => updateSchool(idx, 'contact_first_name', e.target.value)}
                                            error={!!schoolErrFor(idx, 'contact_first_name')}
                                            helperText={schoolErrFor(idx, 'contact_first_name')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Last Name"
                                            value={sc.contact_last_name}
                                            onChange={(e) => updateSchool(idx, 'contact_last_name', e.target.value)}
                                            error={!!schoolErrFor(idx, 'contact_last_name')}
                                            helperText={schoolErrFor(idx, 'contact_last_name')}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <TextField
                                            label="Title / Role (optional)"
                                            value={sc.contact_title}
                                            onChange={(e) => updateSchool(idx, 'contact_title', e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Email"
                                            type="email"
                                            value={sc.contact_email}
                                            onChange={(e) => updateSchool(idx, 'contact_email', e.target.value)}
                                            error={!!schoolErrFor(idx, 'contact_email')}
                                            helperText={schoolErrFor(idx, 'contact_email')}
                                            fullWidth
                                        />
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <PhoneInput
                                            countries={getPhoneCountries()}
                                            defaultCountry="us"
                                            placeholder="School Phone Number"
                                            disableDialCodePrefill
                                            countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                            onChange={(phone, countryIso2) => {
                                                if (!phone) {
                                                    updateSchool(idx, 'institute_phone_country_code', '');
                                                    updateSchool(idx, 'institute_phone_number', '');
                                                    return;
                                                }
                                                const dialCode = getPhoneDialCode(countryIso2);
                                                updateSchool(idx, 'institute_phone_country_code', dialCode);
                                                updateSchool(idx, 'institute_phone_number', splitPhoneByDialCode(phone, dialCode));
                                            }}
                                            inputProps={{ name: `institute_phone_number_${idx}`, autoComplete: 'tel' }}
                                            inputStyle={{ flex: 1, minWidth: 0 }}
                                            style={{
                                                width: '100%',
                                                '--react-international-phone-height': '56px',
                                                '--react-international-phone-font-size': '16px',
                                                '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                            }}
                                        />
                                    </Box>
                                    {/*
                                      * Number of Learners and School Organization Type
                                      * are hidden for a network's school card too, same
                                      * as the single-school form.
                                      */}
                                    {/*
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <TextField
                                            label="Number of Learners (informational)"
                                            type="number"
                                            value={sc.number_of_learners}
                                            onChange={(e) => updateSchool(idx, 'number_of_learners', e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            select
                                            label="School Organization Type"
                                            value={sc.institute_type}
                                            onChange={(e) => updateSchool(idx, 'institute_type', e.target.value)}
                                            fullWidth
                                        >
                                            {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                                                <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    */}
                                    {/* School Primary Role / Activity hidden for a network's school card too. */}
                                    {/*
                                    <TextField
                                        select
                                        label="School Primary Role / Activity"
                                        value={sc.institute_primary_activities}
                                        onChange={(e) => updateSchool(idx, 'institute_primary_activities', e.target.value)}
                                        fullWidth sx={{ mb: 2 }}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => selected
                                                .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                                .join(', '),
                                        }}
                                    >
                                        {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                            <MenuItem key={code} value={code}>
                                                <Checkbox checked={sc.institute_primary_activities.indexOf(code) > -1} />
                                                <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    */}
                                    <TextField
                                        label="School Website (optional)"
                                        value={sc.institute_website}
                                        onChange={(e) => updateSchool(idx, 'institute_website', e.target.value)}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                        autoComplete="url"
                                    />
                                    {/* Additional Capabilities hidden for a network's school card too. */}
                                    {/*
                                    <TextField
                                        select
                                        label="Additional Capabilities"
                                        value={sc.institute_additional_capabilities}
                                        onChange={(e) => updateSchool(idx, 'institute_additional_capabilities', e.target.value)}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                        SelectProps={{
                                            multiple: true,
                                            renderValue: (selected) => selected
                                                .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                                .join(', '),
                                        }}
                                    >
                                        {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                            <MenuItem key={code} value={code}>
                                                <Checkbox checked={sc.institute_additional_capabilities.indexOf(code) > -1} />
                                                <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    */}

                                    {/*
                                      * Combined address field hidden here too — Line 1
                                      * below is the real, required address.
                                      */}
                                    {/*
                                    <TextField
                                        label="School Address"
                                        value={sc.org_address}
                                        onChange={(e) => updateSchool(idx, 'org_address', e.target.value)}
                                        error={!!schoolErrFor(idx, 'org_address')}
                                        helperText={schoolErrFor(idx, 'org_address')}
                                        fullWidth sx={{ mb: 2 }}
                                    />
                                    */}
                                    <TextField
                                        label="School Address — Line 1"
                                        value={sc.institute_address_line1}
                                        onChange={(e) => updateSchool(idx, 'institute_address_line1', e.target.value)}
                                        error={!!schoolErrFor(idx, 'institute_address_line1')}
                                        helperText={schoolErrFor(idx, 'institute_address_line1')}
                                        fullWidth sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        label="School Address — Line 2 (optional)"
                                        value={sc.institute_address_line2}
                                        onChange={(e) => updateSchool(idx, 'institute_address_line2', e.target.value)}
                                        fullWidth sx={{ mb: 2 }}
                                    />
                                    {/* Searchable, like the network's own Country field above —
                                        a plain select can't be typed into. */}
                                    <Autocomplete
                                        options={getCountries()}
                                        getOptionLabel={(option) => option.label || ''}
                                        isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        value={getCountries().find((c) => c.code === sc.org_country) || null}
                                        onChange={(event, newValue) => {
                                            updateSchool(idx, 'org_country', newValue?.code || '');
                                            // The state list is keyed by country — a stale code
                                            // from the previous country would point at the wrong state.
                                            updateSchool(idx, 'institute_state', '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Country"
                                                variant="outlined"
                                                error={!!schoolErrFor(idx, 'org_country')}
                                                helperText={schoolErrFor(idx, 'org_country')}
                                            />
                                        )}
                                        fullWidth
                                        sx={{ mb: 2 }}
                                    />
                                    {/* State/province, below Country since its options depend on
                                        which country is picked. Full width so a long state/province
                                        name doesn't get cropped. */}
                                    {(() => {
                                        const statesForCountry = getStatesByCountry(sc.org_country);
                                        return (
                                            <Autocomplete
                                                options={statesForCountry}
                                                getOptionLabel={(option) => option.name || ''}
                                                isOptionEqualToValue={(option, value) => option.code === value?.code}
                                                value={statesForCountry.find((s) => s.code === sc.institute_state) || null}
                                                onChange={(event, newValue) => {
                                                    updateSchool(idx, 'institute_state', newValue ? newValue.code : '');
                                                }}
                                                disabled={!sc.org_country || statesForCountry.length === 0}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="School State / Province / Region"
                                                        variant="outlined"
                                                        placeholder={
                                                            !sc.org_country
                                                                ? 'Select country first'
                                                                : statesForCountry.length === 0 ? 'N/A' : 'Select state'
                                                        }
                                                    />
                                                )}
                                                fullWidth
                                                sx={{ mb: 2 }}
                                            />
                                        );
                                    })()}
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <TextField
                                            label="City"
                                            value={sc.org_city}
                                            onChange={(e) => updateSchool(idx, 'org_city', e.target.value)}
                                            error={!!schoolErrFor(idx, 'org_city')}
                                            helperText={schoolErrFor(idx, 'org_city')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Postal Code"
                                            value={sc.postal_code}
                                            onChange={(e) => updateSchool(idx, 'postal_code', e.target.value)}
                                            error={!!schoolErrFor(idx, 'postal_code')}
                                            helperText={schoolErrFor(idx, 'postal_code')}
                                            fullWidth
                                        />
                                    </Box>
                                </Box>
                            ))}

                            <Button
                                onClick={addSchool}
                                fullWidth
                                sx={{
                                    textTransform: 'none', fontWeight: 600, mb: 2,
                                    border: '1px solid #FF6900', borderRadius: '10px',
                                    color: '#FF6900',
                                }}
                            >
                                + Add another school
                            </Button>
                        </Box>
                    )}

                    {/* The deck's two notices — slides 35-38. */}
                    <FormNotice sx={{ mb: 1.5 }}>
                        No program is linked automatically. After creating the {isNetwork ? 'network' : 'school'},
                        use <strong>Link Programs</strong> to give it access to the Child Online Safety
                        Assessment or any other program.
                    </FormNotice>
                    {/*
                      * The mock says the activation email goes out on creation.
                      * Ian's note on slides 35 and 38 moves it to publish, and the
                      * AC-01 email itself carries a temporary password rather than
                      * a set-password link — so the wording follows both.
                      */}
                    <FormNotice variant="success" sx={{ mb: 2 }}>
                        {isNetwork
                            ? 'When you publish the workspace, the network contact and each school contact receive an activation email with a link to their workspace and a temporary password to sign in with.'
                            : 'When you publish the workspace, its contact receives an activation email with a link to their workspace and a temporary password to sign in with.'}
                    </FormNotice>
                </Box>
            </DialogContent>
            <DialogActions>
                <Box sx={{ display: 'flex', width: '100%',alignItems:'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: '20px',
                            minWidth: '120px',
                            fontWeight: 'bold',
                            borderColor: '#0097b2',
                            color: '#0097b2',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        sx={{
                            backgroundColor: '#FF6900',
                            color: '#fff',
                            borderRadius: '20px',
                            minWidth: '120px',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: '#FF6900',
                                color: '#ffffff',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'var(--brand-border, #E5E7EB)',
                                color: 'var(--brand-text-muted, #9CA3AF)',
                            },
                        }}
                        // Enabled only once every required field on the form —
                        // including every school card on a network — is filled
                        // and the emails parse.
                        disabled={loading || !isFormValid}
                    >
                        {/*
                          * "Create School" or "Create Network + N School(s)" —
                          * slides 35, 37 and 38, where the count tracks the
                          * repeater.
                          */}
                        {loading
                            ? 'Creating...'
                            : (isNetwork
                                ? (filledSchools.length === 0
                                    ? 'Create Network'
                                    : `Create Network + ${filledSchools.length} ${filledSchools.length === 1 ? 'School' : 'Schools'}`)
                                : (isSchool ? 'Create School' : 'Create'))}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CreateClientDialog;